import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const fetchBrowseLoads = createAsyncThunk(
  'loads/fetchBrowseLoads',
  async ({ page = 1, limit = 10, search = '', mode = '', bidType = '' }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (mode) params.mode = mode.toLowerCase();
      if (bidType) params.bidType = bidType;

      const response = await callAPI(apiUrls.marketplaceShipments, params, 'GET', {});

      if (response?.data?.success) {
        return response?.data?.data;
      }
      return rejectWithValue(response?.data?.message || 'Failed to fetch shipments');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const fetchLoadsStatus = createAsyncThunk(
  'loads/fetchLoadsStatus',
  async ({ page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;

      const response = await callAPI(apiUrls.carrierBids, params, 'GET', {});

      if (response?.data?.success) {
        return response?.data?.data;
      }
      return rejectWithValue(response?.data?.message || 'Failed to fetch bids');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const submitBid = createAsyncThunk(
  'loads/submitBid',
  async ({ shipmentId, amount, remarks, estimatedDeliveryDays }, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.createBid, {}, 'POST', {
        shipmentId,
        amount,
        remarks,
        estimatedDeliveryDays
      });

      if (response?.data?.success) {
        return response?.data?.data;
      }
      return rejectWithValue(response?.data?.message || 'Failed to submit bid');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

const initialState = {
  browseLoads: {
    data: [],
    loading: false,
    error: null,
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  },
  loadsStatus: {
    data: [],
    loading: false,
    error: null,
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  },
  bidSubmission: {
    loading: false,
    error: null,
    success: false
  },
  filters: {
    browse: { search: '', mode: '', bidType: '' },
    status: { search: '' }
  }
};

const loadSlice = createSlice({
  name: 'loads',
  initialState,
  reducers: {
    setBrowseFilters: (state, action) => {
      state.filters.browse = { ...state.filters.browse, ...action.payload };
    },
    setStatusFilters: (state, action) => {
      state.filters.status = { ...state.filters.status, ...action.payload };
    },
    setBrowsePage: (state, action) => {
      state.browseLoads.currentPage = action.payload;
    },
    setStatusPage: (state, action) => {
      state.loadsStatus.currentPage = action.payload;
    },
    resetBidSubmission: (state) => {
      state.bidSubmission.loading = false;
      state.bidSubmission.error = null;
      state.bidSubmission.success = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrowseLoads.pending, (state) => {
        state.browseLoads.loading = true;
        state.browseLoads.error = null;
      })
      .addCase(fetchBrowseLoads.fulfilled, (state, action) => {
        state.browseLoads.loading = false;
        state.browseLoads.data = action.payload?.getData || [];
        state.browseLoads.total = action.payload?.count || 0;
        state.browseLoads.totalPages = action.payload?.totalPages || 1;
      })
      .addCase(fetchBrowseLoads.rejected, (state, action) => {
        state.browseLoads.loading = false;
        state.browseLoads.error = action.payload;
        state.browseLoads.data = [];
      })
      .addCase(fetchLoadsStatus.pending, (state) => {
        state.loadsStatus.loading = true;
        state.loadsStatus.error = null;
      })
      .addCase(fetchLoadsStatus.fulfilled, (state, action) => {
        state.loadsStatus.loading = false;
        state.loadsStatus.data = action.payload?.getData || [];
        state.loadsStatus.total = action.payload?.count || 0;
        state.loadsStatus.totalPages = action.payload?.totalPages || 1;
      })
      .addCase(fetchLoadsStatus.rejected, (state, action) => {
        state.loadsStatus.loading = false;
        state.loadsStatus.error = action.payload;
        state.loadsStatus.data = [];
      })
      .addCase(submitBid.pending, (state) => {
        state.bidSubmission.loading = true;
        state.bidSubmission.error = null;
        state.bidSubmission.success = false;
      })
      .addCase(submitBid.fulfilled, (state) => {
        state.bidSubmission.loading = false;
        state.bidSubmission.success = true;
      })
      .addCase(submitBid.rejected, (state, action) => {
        state.bidSubmission.loading = false;
        state.bidSubmission.error = action.payload;
        state.bidSubmission.success = false;
      });
  }
});

export const {
  setBrowseFilters,
  setStatusFilters,
  setBrowsePage,
  setStatusPage,
  resetBidSubmission
} = loadSlice.actions;

export default loadSlice.reducer;